import {
  COMPATIBILITY_MATCH_TAG,
  KEPT_PAIRING_TAG,
  LEGACY_KEPT_PAIRING_TAG
} from 'utils/compatibility-systems';
import {
  extractFilterMismatchReasons,
  FILTER_MISMATCH_REASONS,
  FILTER_MISMATCH_TAG
} from 'utils/filter-mismatch';

const CUSTOM_TAG = 'Custom';
const ALTERNATIVE_AIR_HANDLERS_TAG = 'Alternative Air Handlers';

const ALTERNATIVE_AIR_HANDLERS_TOOLTIP =
  "One or more zones use an air handler type that doesn't match your preferred selection.";
const FILTER_MISMATCH_LABEL = "Doesn't match filters";

export const FILTER_MISMATCH_TAG_KEY = 'filter-mismatch';
export const RECOMMENDED_TAG_KEY = 'recommended';
export const RECOMMENDED_TAG_LABEL = 'Recommended';

export type TitleTag = {
  key: string;
  label: string;
  color: 'purple' | 'light purple' | 'light green' | 'yellow' | 'default';
  tooltip?: string;
  warningIcon?: boolean;
};

// Soft badge colors render like the system-card chips (pale fill, dark
// text) instead of the solid Tag component.
export const SOFT_BADGE_CLASSES = {
  'light green': 'bg-green-100 text-green-900',
  'light purple': 'bg-purple-100 text-purple-900'
} as const;

export type SoftBadgeColor = keyof typeof SOFT_BADGE_CLASSES;

export function isSoftBadgeColor(
  color: TitleTag['color']
): color is SoftBadgeColor {
  return color in SOFT_BADGE_CLASSES;
}

const KEPT_PAIRING_TAGS: ReadonlySet<string> = new Set([
  KEPT_PAIRING_TAG,
  LEGACY_KEPT_PAIRING_TAG
]);

// The Compatibility Match tag marks every ducted compatibility system, so
// it says nothing that distinguishes one compared system from another.
function getDescriptiveSystemTags(tags: readonly string[]): string[] {
  return tags.filter(
    (tag) =>
      tag !== CUSTOM_TAG &&
      tag !== COMPATIBILITY_MATCH_TAG &&
      tag !== FILTER_MISMATCH_TAG &&
      tag !== ALTERNATIVE_AIR_HANDLERS_TAG &&
      !FILTER_MISMATCH_REASONS.has(tag)
  );
}

export function buildSystemTitleTags(
  tags: readonly string[],
  isTopMatch: boolean,
  isRecommended = false
): TitleTag[] {
  const items: TitleTag[] = [];

  if (isTopMatch) {
    items.push({ key: 'top-match', label: 'Your Top Match', color: 'purple' });
  }

  if (isRecommended) {
    items.push({
      key: RECOMMENDED_TAG_KEY,
      label: RECOMMENDED_TAG_LABEL,
      color: 'light purple'
    });
  }

  getDescriptiveSystemTags(tags).forEach((tag) => {
    // Persisted systems may still carry the pre-rename kept-pairing tag
    // (AHA-6681 display titles): normalize the CHIP label to the new
    // wording; the stored tag value itself is left alone (the key keeps the
    // raw value, so a system somehow carrying both stays collision-free).
    // The kept-pairing chip is the same green badge the system cards and
    // the details modal show.
    const keptPairing = KEPT_PAIRING_TAGS.has(tag);
    items.push({
      key: `tag-${tag}`,
      label: keptPairing ? KEPT_PAIRING_TAG : tag,
      color: keptPairing ? 'light green' : 'default'
    });
  });

  if (tags.includes(ALTERNATIVE_AIR_HANDLERS_TAG)) {
    items.push({
      key: 'alternative-air-handlers',
      label: ALTERNATIVE_AIR_HANDLERS_TAG,
      color: 'yellow',
      tooltip: ALTERNATIVE_AIR_HANDLERS_TOOLTIP
    });
  }

  const mismatchReasons = extractFilterMismatchReasons(tags);
  if (mismatchReasons.length > 0) {
    items.push({
      key: FILTER_MISMATCH_TAG_KEY,
      label: FILTER_MISMATCH_LABEL,
      color: 'yellow',
      tooltip: mismatchReasons.join(', '),
      warningIcon: true
    });
  }

  return items;
}
